import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Button, Popconfirm, Modal, Select, Input, Skeleton, Tooltip } from 'antd';
import {
  TeamOutlined,
  ProjectOutlined,
  CalendarOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ReloadOutlined,
  SunOutlined,
  FileTextOutlined,
  PictureOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  UpOutlined,
  DownOutlined,
  CheckCircleOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import {
  PageHeader,
  SectionCard,
  StatTile,
  Pill,
  PersonAvatar,
  EmptyState,
  FilterChips,
} from '../components/ui';
import ImageLightbox, { LightboxImage } from '../components/ui/ImageLightbox';
import FeedCard from '../components/feeds/FeedCard';
import { dashboardService, DashboardStats, EmployeeWithProjects, EmployeeAttendanceToday } from '../services/dashboardService';
import { projectService, Project } from '../services/projectService';
import ProjectAlerts from '../components/projects/ProjectAlerts';
import { feedService, Feed } from '../services/feedService';
import { leaveRequestService, LeaveRequest } from '../services/leaveRequestService';
import { employeeService, Employee } from '../services/employeeService';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import './DashboardPage.css';

type AttendanceState = 'working' | 'done' | 'absent';
type AttendanceFilter = 'all' | AttendanceState;

// Working first (the people on site right now), then finished for the day, then absent
const ATTENDANCE_ORDER: AttendanceState[] = ['working', 'done', 'absent'];
const TEAM_PREVIEW_COUNT = 8;
const FEEDS_PREVIEW_COUNT = 6;

const attendanceState = (entry: EmployeeAttendanceToday): AttendanceState => {
  if (!entry.first_check_in) return 'absent';
  return entry.last_check_out ? 'done' : 'working';
};

// The on-vacation endpoint loads the employee relation; the type in the service predates that
type VacationEntry = LeaveRequest & { employee?: { name: string; surname: string } };

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [viewer, setViewer] = useState<{ images: LightboxImage[]; index: number; name: string } | null>(null);
  const [selectedProject, setSelectedProject] = useState<number | 'all'>('all');
  const [showAllFeeds, setShowAllFeeds] = useState(false);
  const [assignEmployee, setAssignEmployee] = useState<EmployeeWithProjects | null>(null);
  const [projectToAssign, setProjectToAssign] = useState<number | undefined>();
  const [assigning, setAssigning] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState<EmployeeAttendanceToday[]>([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [attendanceFilter, setAttendanceFilter] = useState<AttendanceFilter>('all');
  const [vacationRequests, setVacationRequests] = useState<VacationEntry[]>([]);
  const [vacationLoading, setVacationLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [teamSearch, setTeamSearch] = useState('');
  const [showAllTeam, setShowAllTeam] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const navigate = useNavigate();

  // Only feeds the start / end alerts, so a failure just leaves them out
  const fetchProjects = useCallback(async () => {
    try {
      setProjects(await projectService.getProjects());
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await dashboardService.getStats());
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error(t('dashboard.fetchStatsError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchTodayAttendance = useCallback(async () => {
    try {
      setTodayAttendance(await dashboardService.getEmployeesAttendanceToday());
    } catch (error) {
      console.error("Failed to fetch today's attendance:", error);
      toast.error(t('dashboard.fetchAttendanceError'));
    } finally {
      setAttendanceLoading(false);
    }
  }, [t]);

  const fetchFeeds = useCallback(async () => {
    try {
      // All of today's feeds in one call: the project filter runs in the browser, so it can
      // offer only the projects that actually have feeds today
      setFeeds(await feedService.getTodaysFeeds());
    } catch (error) {
      console.error('Failed to fetch feeds:', error);
      toast.error(t('dashboard.fetchFeedsError'));
    } finally {
      setFeedsLoading(false);
    }
  }, [t]);

  const fetchVacationRequests = useCallback(async () => {
    try {
      setVacationLoading(true);
      setVacationRequests(await leaveRequestService.onVacationThisMonth(selectedMonth, selectedYear));
    } catch (error) {
      console.error('Failed to fetch vacation requests:', error);
      toast.error(t('dashboard.fetchVacationError'));
    } finally {
      setVacationLoading(false);
    }
  }, [selectedMonth, selectedYear, t]);

  useEffect(() => {
    fetchStats();
    fetchTodayAttendance();
    fetchFeeds();
    fetchProjects();
    employeeService
      .getAll()
      .then(setEmployees)
      .catch((error) => {
        console.error('Failed to fetch employees:', error);
        toast.error(t('dashboard.fetchEmployeesError'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchVacationRequests();
  }, [fetchVacationRequests]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(), fetchTodayAttendance(), fetchFeeds(), fetchVacationRequests(), fetchProjects()]);
    setRefreshing(false);
  };

  // ---------- Attendance ----------
  const attendanceCounts = useMemo(() => {
    const counts: Record<AttendanceState, number> = { working: 0, done: 0, absent: 0 };
    todayAttendance.forEach((entry) => {
      counts[attendanceState(entry)] += 1;
    });
    return counts;
  }, [todayAttendance]);
  const checkedInCount = attendanceCounts.working + attendanceCounts.done;
  const checkedInPercent = todayAttendance.length
    ? Math.round((checkedInCount / todayAttendance.length) * 100)
    : 0;
  const visibleAttendance = useMemo(
    () =>
      todayAttendance
        .filter((entry) => attendanceFilter === 'all' || attendanceState(entry) === attendanceFilter)
        .sort(
          (a, b) =>
            ATTENDANCE_ORDER.indexOf(attendanceState(a)) - ATTENDANCE_ORDER.indexOf(attendanceState(b)) ||
            `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`),
        ),
    [todayAttendance, attendanceFilter],
  );

  // ---------- Feeds ----------
  // Projects that today's feeds belong to, busiest first
  const feedProjects = useMemo(() => {
    const byId = new Map<number, { id: number; name: string; count: number }>();
    feeds.forEach((feed) => {
      if (!feed.project) return;
      const entry = byId.get(feed.project.id);
      if (entry) entry.count += 1;
      else byId.set(feed.project.id, { id: feed.project.id, name: feed.project.name, count: 1 });
    });
    return Array.from(byId.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [feeds]);
  // A refresh can drop the selected project from today's list; fall back to all projects
  const activeFeedProject = feedProjects.find((p) => p.id === selectedProject);
  const filteredFeeds = activeFeedProject
    ? feeds.filter((feed) => feed.project?.id === activeFeedProject.id)
    : feeds;
  const visibleFeeds = showAllFeeds ? filteredFeeds : filteredFeeds.slice(0, FEEDS_PREVIEW_COUNT);

  // ---------- Team & projects ----------
  const team = stats?.activeEmployeesWithProjects ?? [];
  const normalizedTeamSearch = teamSearch.trim().toLowerCase();
  const filteredTeam = team.filter(
    (employee) =>
      !normalizedTeamSearch ||
      `${employee.name} ${employee.surname} ${employee.projects.map((p) => p.name).join(' ')}`
        .toLowerCase()
        .includes(normalizedTeamSearch),
  );
  const visibleTeam = showAllTeam || normalizedTeamSearch ? filteredTeam : filteredTeam.slice(0, TEAM_PREVIEW_COUNT);
  const assignableProjects = assignEmployee
    ? (stats?.availableProjects ?? []).filter((p) => !assignEmployee.projects.some((ap) => ap.id === p.id))
    : [];

  const handleRemoveProject = async (projectId: number, employeeId: number) => {
    try {
      await projectService.removeEmployee(projectId, employeeId);
      toast.success(t('dashboard.removeEmployeeSuccess'));
      await fetchStats();
    } catch (error) {
      toast.error(t('dashboard.removeEmployeeError'));
    }
  };

  const closeAssignModal = () => {
    setAssignEmployee(null);
    setProjectToAssign(undefined);
  };

  const handleAssignProject = async () => {
    if (!assignEmployee || !projectToAssign) return;
    try {
      setAssigning(true);
      await projectService.assignEmployee(projectToAssign, assignEmployee.id);
      toast.success(t('dashboard.assignEmployeeSuccess'));
      await fetchStats();
      closeAssignModal();
    } catch (error) {
      toast.error(t('dashboard.assignEmployeeError'));
    } finally {
      setAssigning(false);
    }
  };

  // ---------- Formatting ----------
  const formatTime = (value: string) => dayjs(value).format('HH:mm');
  const formatDay = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const monthLabel = new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString(i18n.language, {
    month: 'long',
    year: 'numeric',
  });
  const now = new Date();
  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();
  const hour = now.getHours();
  const greeting =
    hour < 12 ? t('dashboard.greetingMorning') : hour < 18 ? t('dashboard.greetingAfternoon') : t('dashboard.greetingEvening');
  const todayLabel = now.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const shiftMonth = (delta: number) => {
    const date = new Date(selectedYear, selectedMonth - 1 + delta, 1);
    setSelectedMonth(date.getMonth() + 1);
    setSelectedYear(date.getFullYear());
  };

  const employeeName = (request: VacationEntry) => {
    if (request.employee) return `${request.employee.name} ${request.employee.surname}`;
    const employee = employees.find((e) => e.id === request.employee_id);
    return employee ? `${employee.name} ${employee.surname}` : '—';
  };

  const leaveTypeLabel = (type: string) =>
    type === 'Jahresurlaub' ? t('vacations.annualLeave') : type === 'Krankheitsurlaub' ? t('vacations.sickLeave') : type;

  const pendingLeave = stats?.leaveRequests ?? 0;

  return (
    <MainLayout bare>
      <PageHeader
        title={greeting}
        subtitle={todayLabel}
        actions={
          <Button icon={<ReloadOutlined spin={refreshing} />} onClick={handleRefresh} disabled={refreshing}>
            {t('dashboard.refresh')}
          </Button>
        }
      />

      {/* Projects starting / ending soon or overdue — hidden when there are none */}
      <ProjectAlerts
        projects={projects}
        onSelect={(projectId) => navigate(`/projects?project=${projectId}`)}
        action={
          <Link to="/projects" className="e22-link-button">
            {t('dashboard.viewAll')} <RightOutlined style={{ fontSize: 10 }} />
          </Link>
        }
      />

      {/* Key numbers */}
      <Row gutter={[16, 16]}>
        <Col xs={12} lg={6}>
          <StatTile
            label={t('dashboard.activeEmployees')}
            value={stats?.activeEmployees ?? 0}
            icon={<TeamOutlined />}
            tone="primary"
            to="/employees"
            loading={loading}
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatTile
            label={t('dashboard.activeProjects')}
            value={stats?.activeProjects ?? 0}
            icon={<ProjectOutlined />}
            tone="success"
            to="/projects"
            loading={loading}
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatTile
            label={t('dashboard.onVacationToday')}
            value={stats?.totalEmployeesOnVacationToday ?? 0}
            icon={<SunOutlined />}
            tone="warning"
            to="/vacation"
            loading={loading}
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatTile
            label={t('dashboard.pendingLeaveRequests')}
            value={pendingLeave}
            icon={<FileTextOutlined />}
            tone={pendingLeave > 0 ? 'warning' : 'violet'}
            to="/vacation"
            loading={loading}
            alert={pendingLeave > 0}
            footer={pendingLeave > 0 ? t('dashboard.waitingForApproval') : undefined}
          />
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        {/* Today's attendance */}
        <Col xs={24} xl={15}>
          <SectionCard
            title={t('dashboard.todaysAttendance')}
            icon={<ClockCircleOutlined />}
            subtitle={
              todayAttendance.length > 0
                ? t('dashboard.checkedInSummary', { checkedIn: checkedInCount, total: todayAttendance.length })
                : undefined
            }
            action={
              <Link to="/attendance" className="e22-link-button">
                {t('dashboard.viewAll')} <RightOutlined style={{ fontSize: 10 }} />
              </Link>
            }
          >
            {attendanceLoading ? (
              <Skeleton active avatar paragraph={{ rows: 4 }} />
            ) : todayAttendance.length === 0 ? (
              <EmptyState icon={<TeamOutlined />} text={t('dashboard.noAttendanceToday')} />
            ) : (
              <>
                <div
                  className="e22-progress"
                  role="progressbar"
                  aria-valuenow={checkedInPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('dashboard.checkedInSummary', { checkedIn: checkedInCount, total: todayAttendance.length })}
                >
                  <div className="e22-progress-fill" style={{ width: `${checkedInPercent}%` }} />
                </div>
                <FilterChips<AttendanceFilter>
                  ariaLabel={t('dashboard.todaysAttendance')}
                  value={attendanceFilter}
                  onChange={setAttendanceFilter}
                  options={[
                    { key: 'all', label: t('dashboard.filterAll'), count: todayAttendance.length },
                    { key: 'working', label: t('dashboard.filterWorking'), count: attendanceCounts.working },
                    { key: 'done', label: t('dashboard.filterDone'), count: attendanceCounts.done },
                    { key: 'absent', label: t('dashboard.filterAbsent'), count: attendanceCounts.absent },
                  ]}
                />
                {visibleAttendance.length === 0 ? (
                  <EmptyState icon={<TeamOutlined />} text={t('dashboard.nobodyInFilter')} />
                ) : (
                  <div className="e22-scroll db-attendance-grid">
                    {visibleAttendance.map((entry, index) => {
                      const state = attendanceState(entry);
                      return (
                        <div key={`${entry.name}-${entry.surname}-${index}`} className="db-attendance-item">
                          <PersonAvatar name={entry.name} surname={entry.surname} size={36} muted={state === 'absent'} />
                          <div className="e22-row-body">
                            <div className="e22-row-title">
                              {entry.name} {entry.surname}
                            </div>
                          </div>
                          {state === 'working' && entry.first_check_in && (
                            <Pill tone="success" live>
                              {t('dashboard.workingSince', { time: formatTime(entry.first_check_in) })}
                            </Pill>
                          )}
                          {state === 'done' && entry.first_check_in && entry.last_check_out && (
                            <Pill>
                              {formatTime(entry.first_check_in)} – {formatTime(entry.last_check_out)}
                            </Pill>
                          )}
                          {state === 'absent' && <Pill tone="danger">{t('dashboard.notCheckedInToday')}</Pill>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={9}>
          {/* This month */}
          <section className="e22-card db-month">
            <div className="db-month-title">{t('dashboard.thisMonth')}</div>
            <div className="db-month-row">
              {[
                { value: stats?.thisMonthProjects, label: t('dashboard.projectsCreated'), icon: <ProjectOutlined /> },
                { value: stats?.thisMonthFinishedProjects, label: t('dashboard.projectsFinished'), icon: <CheckCircleOutlined /> },
                { value: stats?.totalPublicHolidaysThisMonth, label: t('dashboard.publicHolidays'), icon: <FlagOutlined /> },
              ].map((item) => (
                <div key={item.label} className="db-month-item">
                  <div className="db-month-value">{loading ? '–' : item.value ?? 0}</div>
                  <div className="db-month-label">
                    <span aria-hidden>{item.icon}</span> {item.label}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* On vacation */}
          <SectionCard
            className="db-vacation"
            title={t('dashboard.employeesOnVacation')}
            icon={<SunOutlined />}
            action={
              <div className="db-stepper">
                <Tooltip title={t('dashboard.previousMonth')}>
                  <Button
                    size="small"
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={() => shiftMonth(-1)}
                    aria-label={t('dashboard.previousMonth')}
                  />
                </Tooltip>
                <span className="db-stepper-label">{monthLabel}</span>
                <Tooltip title={t('dashboard.nextMonth')}>
                  <Button
                    size="small"
                    type="text"
                    icon={<RightOutlined />}
                    onClick={() => shiftMonth(1)}
                    aria-label={t('dashboard.nextMonth')}
                  />
                </Tooltip>
              </div>
            }
          >
            {!isCurrentMonth && (
              <button
                type="button"
                className="e22-link-button db-back-to-now"
                onClick={() => {
                  setSelectedMonth(now.getMonth() + 1);
                  setSelectedYear(now.getFullYear());
                }}
              >
                {t('dashboard.backToThisMonth')}
              </button>
            )}
            {vacationLoading ? (
              <Skeleton active avatar paragraph={{ rows: 2 }} />
            ) : vacationRequests.length === 0 ? (
              <EmptyState icon={<SunOutlined />} text={t('dashboard.noEmployeesOnVacation')} />
            ) : (
              <div className="e22-scroll" style={{ maxHeight: 320 }}>
                {vacationRequests.map((request) => {
                  const name = employeeName(request);
                  const [first, ...rest] = name.split(' ');
                  return (
                    <div key={request.id} className="e22-row">
                      <PersonAvatar name={first} surname={rest.join(' ')} size={36} />
                      <div className="e22-row-body">
                        <div className="e22-row-title">{name}</div>
                        <div className="e22-row-subtitle">
                          {formatDay(request.start_date)} – {formatDay(request.end_date)}
                          {request.vacation_days
                            ? ` · ${t('dashboard.workingDays', { count: request.vacation_days })}`
                            : ''}
                        </div>
                      </div>
                      <Pill tone={request.type === 'Krankheitsurlaub' ? 'danger' : 'warning'}>
                        {leaveTypeLabel(request.type)}
                      </Pill>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        {/* Today's feeds */}
        <Col xs={24} xl={15}>
          <SectionCard
            title={t('dashboard.todaysFeeds')}
            icon={<PictureOutlined />}
            subtitle={feeds.length > 0 ? t('dashboard.feedCount', { count: feeds.length }) : undefined}
            action={
              <Link to="/feeds" className="e22-link-button">
                {t('dashboard.viewAll')} <RightOutlined style={{ fontSize: 10 }} />
              </Link>
            }
          >
            {feedsLoading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : feeds.length === 0 ? (
              <EmptyState icon={<PictureOutlined />} text={t('dashboard.noFeedsToday')} />
            ) : (
              <>
                {/* Filtering only makes sense once feeds come from more than one project */}
                {feedProjects.length > 1 && (
                  <FilterChips<number | 'all'>
                    ariaLabel={t('dashboard.filterByProject')}
                    value={activeFeedProject ? activeFeedProject.id : 'all'}
                    onChange={(key) => {
                      setSelectedProject(key);
                      setShowAllFeeds(false);
                    }}
                    options={[
                      { key: 'all', label: t('dashboard.allProjects'), count: feeds.length },
                      ...feedProjects.map((project) => ({ key: project.id, label: project.name, count: project.count })),
                    ]}
                  />
                )}
                <div className="db-feeds">
                  {visibleFeeds.map((feed) => (
                    <FeedCard
                      key={feed.id}
                      feed={feed}
                      thumbSize={88}
                      onOpenImage={(index) =>
                        setViewer({
                          images: (feed.imageUrls ?? []).map((url, imageIndex) => ({
                            full: url,
                            thumb: feed.thumbnailUrls?.[imageIndex],
                          })),
                          index,
                          name: [feed.project?.name, new Date(feed.created_at).toISOString().slice(0, 10)]
                            .filter(Boolean)
                            .join(' '),
                        })
                      }
                    />
                  ))}
                </div>
                {filteredFeeds.length > FEEDS_PREVIEW_COUNT && (
                  <div className="e22-show-more">
                    <button type="button" className="e22-link-button" onClick={() => setShowAllFeeds(!showAllFeeds)}>
                      {showAllFeeds ? t('dashboard.showLess') : t('dashboard.showAll', { count: filteredFeeds.length })}
                      {showAllFeeds ? <UpOutlined /> : <DownOutlined />}
                    </button>
                  </div>
                )}
              </>
            )}
          </SectionCard>
        </Col>

        {/* Team & projects */}
        <Col xs={24} xl={9}>
          <SectionCard
            title={t('dashboard.activeEmployeesWithProjects')}
            icon={<TeamOutlined />}
            subtitle={team.length > 0 ? t('dashboard.teamSummary', { count: team.length }) : undefined}
          >
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 4 }} />
            ) : team.length === 0 ? (
              <EmptyState icon={<TeamOutlined />} text={t('dashboard.noActiveEmployees')} />
            ) : (
              <>
                {team.length > 6 && (
                  <Input
                    allowClear
                    prefix={<SearchOutlined style={{ color: 'var(--e22-text-muted)' }} />}
                    placeholder={t('dashboard.searchTeam')}
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                )}
                {filteredTeam.length === 0 ? (
                  <EmptyState icon={<SearchOutlined />} text={t('dashboard.noMatchingEmployees')} />
                ) : (
                  visibleTeam.map((employee) => (
                    <div key={employee.id} className="e22-row db-team-row">
                      <PersonAvatar name={employee.name} surname={employee.surname} size={36} />
                      <div className="e22-row-body">
                        <div className="e22-row-title">
                          {employee.name} {employee.surname}
                        </div>
                        <div className="db-chips">
                          {employee.projects.length === 0 ? (
                            <span className="e22-row-subtitle">{t('dashboard.noProjectsAssigned')}</span>
                          ) : (
                            employee.projects.map((project) => (
                              <span key={project.id} className="e22-chip">
                                <span className="e22-chip-label">{project.name}</span>
                                <Popconfirm
                                  title={t('dashboard.removeEmployeeFromProject')}
                                  description={`${employee.name} ${employee.surname} · ${project.name}`}
                                  onConfirm={() => handleRemoveProject(project.id, employee.id)}
                                  okText={t('common.yes')}
                                  cancelText={t('common.no')}
                                  okButtonProps={{ danger: true }}
                                >
                                  <button
                                    type="button"
                                    className="e22-chip-remove"
                                    aria-label={`${t('dashboard.removeEmployeeFromProject')} ${project.name}`}
                                  >
                                    <CloseOutlined />
                                  </button>
                                </Popconfirm>
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                      <Tooltip title={t('dashboard.assignProject')}>
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => setAssignEmployee(employee)}
                          aria-label={`${t('dashboard.assignProject')}: ${employee.name} ${employee.surname}`}
                        >
                          {t('dashboard.assign')}
                        </Button>
                      </Tooltip>
                    </div>
                  ))
                )}
                {!normalizedTeamSearch && filteredTeam.length > TEAM_PREVIEW_COUNT && (
                  <div className="e22-show-more">
                    <button type="button" className="e22-link-button" onClick={() => setShowAllTeam(!showAllTeam)}>
                      {showAllTeam ? t('dashboard.showLess') : t('dashboard.showAll', { count: filteredTeam.length })}
                      {showAllTeam ? <UpOutlined /> : <DownOutlined />}
                    </button>
                  </div>
                )}
              </>
            )}
          </SectionCard>
        </Col>
      </Row>

      <Modal
        open={!!assignEmployee}
        title={t('dashboard.assignProject')}
        onCancel={closeAssignModal}
        onOk={handleAssignProject}
        okText={t('dashboard.assign')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: !projectToAssign }}
        confirmLoading={assigning}
        destroyOnClose
      >
        {assignEmployee && (
          <div className="db-assign">
            <div className="e22-row" style={{ paddingTop: 0 }}>
              <PersonAvatar name={assignEmployee.name} surname={assignEmployee.surname} size={40} />
              <div className="e22-row-body">
                <div className="e22-row-title">
                  {assignEmployee.name} {assignEmployee.surname}
                </div>
                <div className="e22-row-subtitle">
                  {t('dashboard.currentProjects', { count: assignEmployee.projects.length })}
                </div>
              </div>
            </div>
            {assignableProjects.length === 0 ? (
              <EmptyState icon={<CalendarOutlined />} text={t('dashboard.noAvailableProjects')} />
            ) : (
              <Select
                autoFocus
                showSearch
                optionFilterProp="label"
                placeholder={t('dashboard.chooseProject')}
                value={projectToAssign}
                onChange={setProjectToAssign}
                style={{ width: '100%' }}
                options={assignableProjects.map((project) => ({ value: project.id, label: project.name }))}
              />
            )}
          </div>
        )}
      </Modal>

      {viewer && (
        <ImageLightbox
          images={viewer.images}
          startIndex={viewer.index}
          downloadName={viewer.name}
          onClose={() => setViewer(null)}
          labels={{
            close: t('common.close'),
            previous: t('feeds.previousPhoto'),
            next: t('feeds.nextPhoto'),
            openOriginal: t('feeds.openOriginal'),
            download: t('feeds.downloadPhoto'),
          }}
        />
      )}
    </MainLayout>
  );
};

export default DashboardPage;
